import cls from 'classnames';
import { clamp } from 'lodash';
import PropTypes from 'prop-types';
import React from 'react';

import { LoaderIcon } from '../../assets/icons';
import { RCEHelpersPropTypes, withRCEHelpers } from '../rce-helpers-context';
import styles from './text-field.scss';

class TextFieldComponent extends React.PureComponent {
  $el = React.createRef();

  state = {
    placeholder: this.props.placeholder,
    rows: 1,
    syncing: false,
    isFocused: false,
  };

  componentDidMount() {
    const { rce } = this.props;
    const isEditMode = !rce.isViewMode;

    if (isEditMode) {
      this.resize();
    }

    this.handleAutoFocus();

    window?.addEventListener('resize', this.handleWindowResizeEvent);
  }

  componentDidUpdate(props) {
    if (this.props.rce.isViewMode === props.rce.isViewMode) {
      return;
    }

    this.handleAutoFocus();
  }

  handleAutoFocus() {
    const { rce, autofocus } = this.props;
    const isEditMode = !rce.isViewMode;

    if (autofocus && isEditMode) {
      this.$el.current?.focus();
    }
  }

  componentWillUnmount() {
    window?.removeEventListener('resize', this.handleWindowResizeEvent);
  }

  handleWindowResizeEvent = () => setTimeout(() => this.resize());

  hidePlaceholder() {
    this.setState({ placeholder: '' });
  }

  showPlaceholder() {
    this.setState({ placeholder: this.props.placeholder });
  }

  handleFocus = e => {
    e.stopPropagation();
    setTimeout(() => this.resize());
    this.hidePlaceholder();
    this.props.rce.setInPluginEditingMode(true);
    this.setState({ isFocused: true });
  };

  handleKeyDown(event) {
    if (event.keyCode === 13) {
      event.preventDefault();
    }
  }

  handleBlur = () => {
    setTimeout(() => this.resize());
    this.showPlaceholder();
    this.props.rce.setInPluginEditingMode(false);
    this.setState({ isFocused: false });
  };

  handleChange = event => {
    this.props.onChange(event.target.value);

    this.resize();
  };

  resize() {
    if (!this.$el.current) {
      return;
    }

    const computedStyles = getComputedStyle(this.$el.current);

    const lineHeight = parseInt(computedStyles.lineHeight);
    const paddingTop = parseInt(computedStyles.paddingTop);
    const paddingBottom = parseInt(computedStyles.paddingBottom);

    this.$el.current.rows = 1;

    const { scrollHeight } = this.$el.current;

    this.$el.current.rows = this.state.rows;

    const contentHeight = scrollHeight - (paddingTop + paddingBottom);
    const rows = Math.ceil(clamp(contentHeight / lineHeight, 1, Infinity));

    this.setState({ rows });
  }

  render() {
    const {
      dataHook,
      textAutoResize,
      className,
      rce,
      endAdornment,
      style,
      children,
      startAdornment,
      maxLength,
      disabled,
      value,
    } = this.props;

    const { placeholder, rows, syncing, isFocused } = this.state;

    if (rce.isViewMode || rce.isPreview) {
      return (
        <>
          <p
            style={style}
            className={cls(
              styles.text,
              className,
              textAutoResize && {
                [styles.small]: value.length > 120,
                [styles.medium]: value.length <= 120,
                [styles.large]: value.length <= 80,
              },
              {
                [styles.webview]: rce.isWebView,
              }
            )}
          >
            {startAdornment}
            {children}
            <span className={styles.value} data-hook={dataHook}>
              {this.props.value || this.props.placeholder}
            </span>
          </p>
        </>
      );
    }

    return (
      <div className={styles.root}>
        {startAdornment}
        <textarea
          data-hook={dataHook}
          disabled={disabled}
          maxLength={maxLength}
          style={style}
          ref={this.$el}
          onChange={this.handleChange}
          onFocus={this.handleFocus}
          onBlur={this.handleBlur}
          placeholder={placeholder}
          value={value}
          rows={rows}
          onKeyDown={this.handleKeyDown}
          className={cls(
            styles.input,
            className,
            textAutoResize && {
              [styles.small]: value.length > 120,
              [styles.medium]: value.length <= 120,
              [styles.large]: value.length <= 80,
            }
          )}
          onClick={undefined}
        />
        <span
          className={cls(styles.endAdornment, {
            [styles.shown]: syncing || isFocused,
          })}
        >
          {syncing ? <LoaderIcon className={styles.spinner} /> : endAdornment}
        </span>
      </div>
    );
  }
}

export const TextField = withRCEHelpers(TextFieldComponent);

TextFieldComponent.defaultProps = {
  maxLength: 500,
  disabled: false,
};

TextFieldComponent.propTypes = {
  dataHook: PropTypes.string,
  autofocus: PropTypes.bool,
  maxLength: PropTypes.number,
  value: PropTypes.string,
  disabled: PropTypes.string,
  className: PropTypes.string,
  onChange: PropTypes.func,
  placeholder: PropTypes.string.isRequired,
  textAutoResize: PropTypes.bool,
  startAdornment: PropTypes.node,
  endAdornment: PropTypes.node,
  ...RCEHelpersPropTypes,
};
